import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { nextBadgeNumber, isTimestampBadge } from '../utils/badgeNumber';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../config/firebase';
import { User, UserInvite } from '../models/User';

export class UserService {
  private static instance: UserService;
  private usersCollection = collection(db, 'users');

  private constructor() {}

  static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  async createUser(
    userId: string,
    companyId: string,
    data: UserInvite
  ): Promise<User> {
    const userRef = doc(this.usersCollection, userId);
    const now = new Date();

    // Assign a clean sequential badge (VERA-001, ...) when none is supplied,
    // or when an admin left it as a legacy timestamp value.
    const providedBadge = data.badgeNumber?.trim();
    const badgeNumber =
      providedBadge && !isTimestampBadge(providedBadge)
        ? providedBadge
        : await nextBadgeNumber();

    const user: User = {
      id: userId,
      companyId,
      email: data.email,
      name: data.name,
      role: data.role,
      supervisorId: data.supervisorId,
      credentials: data.credentials,
      specializations: data.specializations,
      badgeNumber,
      department: data.department,
      createdAt: now,
      status: 'active',
    };

    // Filter out undefined values before sending to Firestore
    const firestoreData: any = {
      id: userId,
      companyId,
      email: data.email,
      name: data.name,
      role: data.role,
      createdAt: Timestamp.fromDate(now),
      status: 'active',
    };

    // Only add optional fields if they have defined values
    if (data.supervisorId !== undefined) {
      firestoreData.supervisorId = data.supervisorId;
    }
    if (data.credentials !== undefined) {
      firestoreData.credentials = data.credentials;
    }
    if (data.specializations !== undefined) {
      firestoreData.specializations = data.specializations;
    }
    firestoreData.badgeNumber = badgeNumber;
    if (data.department !== undefined) {
      firestoreData.department = data.department;
    }

    await setDoc(userRef, firestoreData);

    return user;
  }

  async getUser(userId: string): Promise<User | null> {
    const userRef = doc(this.usersCollection, userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      console.log('UserService: User document does not exist for userId:', userId);
      return null;
    }

    const data = userSnap.data();
    console.log('UserService: Raw user data from Firestore:', data);
    console.log('UserService: CompanyId field value:', data.companyId);
    console.log('UserService: All fields:', Object.keys(data));

    return {
      id: userSnap.id,
      companyId: data.companyId,
      email: data.email,
      name: data.name,
      role: data.role,
      supervisorId: data.supervisorId,
      credentials: data.credentials,
      specializations: data.specializations,
      status: data.status,
      permissions: data.permissions,
      badgeNumber: data.badgeNumber,
      department: data.department,
      assistantVoice: data.assistantVoice,
      assistantVoiceEnabled: data.assistantVoiceEnabled,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate(),
      lastLogin: data.lastLogin?.toDate(),
    } as User;
  }

  async updateUser(userId: string, updates: Partial<User>): Promise<void> {
    const userRef = doc(this.usersCollection, userId);
    // Firestore's updateDoc rejects undefined values. Drop any undefined
    // fields (e.g. an empty optional hireDate) so the update doesn't fail;
    // omitted fields are simply left unchanged.
    const clean: Record<string, any> = {};
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined) clean[key] = value;
    });
    await updateDoc(userRef, clean);
  }

  async updateLastLogin(userId: string): Promise<void> {
    const userRef = doc(this.usersCollection, userId);
    await updateDoc(userRef, {
      lastLogin: Timestamp.fromDate(new Date()),
    });
  }

  async getUsersByCompany(companyId: string): Promise<User[]> {
    const q = query(
      this.usersCollection,
      where('companyId', '==', companyId)
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        companyId: data.companyId,
        email: data.email,
        name: data.name,
        role: data.role,
        supervisorId: data.supervisorId,
        credentials: data.credentials,
        specializations: data.specializations,
        status: data.status,
        permissions: data.permissions,
        badgeNumber: data.badgeNumber,
        department: data.department,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate(),
        lastLogin: data.lastLogin?.toDate(),
      } as User;
    });
  }

  async setUserStatus(
    userId: string,
    status: 'active' | 'inactive' | 'suspended'
  ): Promise<void> {
    const userRef = doc(this.usersCollection, userId);
    await updateDoc(userRef, { status });
  }

  // Get all supervisors in a company
  async getSupervisorsByCompany(companyId: string): Promise<User[]> {
    const q = query(
      this.usersCollection,
      where('companyId', '==', companyId),
      where('role', '==', 'supervisor')
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        companyId: data.companyId,
        email: data.email,
        name: data.name,
        role: data.role,
        supervisorId: data.supervisorId,
        credentials: data.credentials,
        specializations: data.specializations,
        status: data.status,
        badgeNumber: data.badgeNumber,
        department: data.department,
        createdAt: data.createdAt?.toDate() || new Date(),
        lastLogin: data.lastLogin?.toDate(),
      } as User;
    });
  }

  // Get all RBTs assigned to a specific supervisor
  async getRBTsBySupervisor(supervisorId: string): Promise<User[]> {
    const q = query(
      this.usersCollection,
      where('supervisorId', '==', supervisorId),
      where('role', '==', 'rbt')
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        companyId: data.companyId,
        email: data.email,
        name: data.name,
        role: data.role,
        supervisorId: data.supervisorId,
        credentials: data.credentials,
        specializations: data.specializations,
        status: data.status,
        badgeNumber: data.badgeNumber,
        department: data.department,
        createdAt: data.createdAt?.toDate() || new Date(),
        lastLogin: data.lastLogin?.toDate(),
      } as User;
    });
  }

  // Assign an RBT to a supervisor
  async assignSupervisor(rbtId: string, supervisorId: string): Promise<void> {
    const userRef = doc(this.usersCollection, rbtId);
    await updateDoc(userRef, { supervisorId });
  }

  // Remove supervisor assignment (for reassignment or when supervisor leaves)
  async removeSupervisor(rbtId: string): Promise<void> {
    const userRef = doc(this.usersCollection, rbtId);
    await updateDoc(userRef, { supervisorId: null });
  }

  // Delete a user (Firestore document only - Firebase Auth user remains)
  async deleteUser(userId: string): Promise<void> {
    // Prefer the admin callable, which removes BOTH the Auth account and the
    // Firestore doc. Fall back to a Firestore-only delete if the function
    // isn't deployed/available (never worse than the previous behavior).
    try {
      const callable = httpsCallable<{ userId: string }, { success: boolean }>(
        getFunctions(),
        'deleteUserAccount'
      );
      await callable({ userId });
      return;
    } catch (error) {
      console.error(
        'deleteUserAccount function unavailable — removing Firestore doc only (Auth account remains):',
        error
      );
      await deleteDoc(doc(this.usersCollection, userId));
    }
  }

  // Create a new user with Firebase Auth and Firestore document
  // The provisionUser Cloud Function creates the Auth account + user doc in the
  // admin's own facility (security rules forbid a browser from setting
  // role/companyId). The admin stays signed in. companyId is kept in the
  // signature for callers, but the server always uses the admin's own facility.
  async inviteUser(_companyId: string, userData: UserInvite): Promise<void> {
    try {
      const provision = httpsCallable<UserInvite, { success: boolean; userId: string }>(
        getFunctions(),
        'provisionUser'
      );
      await provision(userData);

      // Email the new user a set-your-password link. If this fails, they can
      // still use "Forgot Password" at login.
      try {
        await sendPasswordResetEmail(auth, userData.email);
      } catch (emailError) {
        console.error('Invite email failed — user can use "Forgot Password" at login:', emailError);
      }
    } catch (error) {
      console.error('Error inviting user:', error);
      throw error;
    }
  }

  hasPermission(user: User, permission: string): boolean {
    const rolePermissions: Record<string, string[]> = {
      admin: ['all'],
      supervisor: [
        'view_all_sessions',
        'review_sessions',
        'view_footage',
        'upload_footage',
        'create_incident',
        'invite_users',
        'manage_rbts',
      ],
      rbt: ['view_own_sessions', 'upload_footage', 'view_footage', 'create_incident'],
      viewer: ['view_footage'],
    };

    const permissions = rolePermissions[user.role] || [];
    return permissions.includes('all') || permissions.includes(permission);
  }
}

// Singleton instance
export const userService = UserService.getInstance();
